<template>
  <div class="notification-settings">
    <div class="container">
      <PageHeader
        :title="$t('settings.weekly_digest.page_title')"
        :description="$t('settings.weekly_digest.page_description')"
      />

      <div class="notification-settings__intro">
        <BaseIcon name="info" size="16" />
        <p>{{ $t('settings.weekly_digest.intro') }}</p>
      </div>

      <div v-if="loading" class="notification-settings__loading">
        <LoadingSpinner size="sm" />
      </div>

      <EmptyState
        v-else-if="organizations.length === 0"
        icon="envelope"
        :title="$t('settings.weekly_digest.section_title')"
        :description="$t('settings.weekly_digest.empty')"
        size="sm"
      />

      <div v-else class="notification-settings__list">
        <div
          v-for="org in organizations"
          :key="org.id"
          class="org-card"
          :class="{ 'org-card--current': org.id === currentOrganizationId }"
        >
          <div class="org-card__header">
            <div class="org-card__heading">
              <h3 class="org-card__name">{{ org.name }}</h3>
              <span class="org-card__role" :class="'org-card__role--' + org.role">
                {{ roleLabel(org.role) }}
              </span>
              <span v-if="org.id === currentOrganizationId" class="org-card__badge">
                {{ $t('settings.weekly_digest.current_org_badge') }}
              </span>
            </div>
            <span
              class="org-card__status"
              :class="{ 'org-card__status--on': org.weekly_digest_enabled }"
            >
              {{
                org.weekly_digest_enabled
                  ? $t('settings.weekly_digest.status_on')
                  : $t('settings.weekly_digest.status_off')
              }}
            </span>
          </div>

          <div class="org-card__body">
            <ToggleInput
              :id="'weekly-digest-' + org.id"
              :label="$t('settings.weekly_digest.toggle_label')"
              :modelValue="!!org.weekly_digest_enabled"
              :disabled="!isEditable(org) || savingOrgId === org.id"
              @update:modelValue="(value) => onToggle(org, value)"
            />
            <p class="org-card__helper">
              {{ $t('settings.weekly_digest.helper', { orgName: org.name }) }}
            </p>

            <p v-if="isSuperAdmin(org)" class="org-card__note org-card__note--muted">
              {{ $t('settings.weekly_digest.super_admin_note') }}
            </p>

            <div v-else-if="org.id !== currentOrganizationId" class="org-card__switch">
              <p class="org-card__note">{{ $t('settings.weekly_digest.switch_to_edit') }}</p>
              <Btn
                :text="$t('settings.weekly_digest.switch_action')"
                variant="outline"
                size="sm"
                :loading="switchingOrgId === org.id"
                :disabled="switchingOrgId !== null"
                @click="switchAndEdit(org)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, getCurrentInstance } from 'vue';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import Btn from '@/components/partials/Btn.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { useUserStore } from '@/store/userStore';
  import { useToastStore } from '@/store/toastStore';

  export default {
    name: 'SettingsNotificationsView',

    components: {
      PageHeader,
      Btn,
      LoadingSpinner,
      EmptyState,
      ToggleInput,
      BaseIcon,
    },

    setup() {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const userStore = useUserStore();
      const toastStore = useToastStore();

      const organizations = ref([]);
      const loading = ref(true);
      const savingOrgId = ref(null);
      const switchingOrgId = ref(null);

      const currentOrganizationId = computed(() => userStore.currentOrganizationId);

      const isSuperAdmin = (org) => org.role === 'super_admin';
      const isEditable = (org) => org.id === currentOrganizationId.value && !isSuperAdmin(org);

      const roleLabel = (role) => {
        const key = `settings.weekly_digest.roles.${role}`;
        const label = $t(key);
        return label === key ? role : label;
      };

      const normalizeList = (data) => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.organizations)) return data.organizations;
        if (Array.isArray(data?.data)) return data.data;
        return [];
      };

      const loadOrganizations = async () => {
        loading.value = true;
        try {
          const data = await userStore.fetchUserOrganizations();
          organizations.value = normalizeList(data);
        } catch (error) {
          console.error('Failed to load notification settings:', error);
          toastStore.addToast({
            type: 'error',
            message: error.response?.data?.error || $t('settings.weekly_digest.load_error'),
          });
          organizations.value = [];
        } finally {
          loading.value = false;
        }
      };

      const onToggle = async (org, enabled) => {
        if (!isEditable(org) || savingOrgId.value === org.id) return;

        const previous = org.weekly_digest_enabled;
        org.weekly_digest_enabled = enabled; // optimistic
        savingOrgId.value = org.id;

        try {
          // Send the full notification_settings object so the backend's shallow
          // merge never clobbers unknown keys we round-tripped.
          const data = await userStore.updateNotificationSettings({
            ...(org.notification_settings || {}),
            weekly_digest: { enabled },
          });
          org.notification_settings = data.notification_settings;
          org.weekly_digest_enabled = data.weekly_digest_enabled;
          toastStore.addToast({
            type: 'success',
            message: $t('settings.weekly_digest.save_success'),
          });
        } catch (error) {
          org.weekly_digest_enabled = previous; // revert
          console.error('Failed to update notification settings:', error);
          toastStore.addToast({
            type: 'error',
            message: error.response?.data?.error || $t('settings.weekly_digest.save_error'),
          });
        } finally {
          savingOrgId.value = null;
        }
      };

      const switchAndEdit = async (org) => {
        if (switchingOrgId.value) return;
        switchingOrgId.value = org.id;
        try {
          await userStore.switchOrganization(org.id);
          toastStore.addToast({
            type: 'success',
            message: $t('settings.weekly_digest.switch_success', { orgName: org.name }),
          });
        } catch (error) {
          console.error('Failed to switch organization:', error);
          toastStore.addToast({
            type: 'error',
            message: error.response?.data?.error || $t('settings.weekly_digest.switch_error'),
          });
        } finally {
          switchingOrgId.value = null;
        }
      };

      onMounted(loadOrganizations);

      return {
        organizations,
        loading,
        savingOrgId,
        switchingOrgId,
        currentOrganizationId,
        isSuperAdmin,
        isEditable,
        roleLabel,
        onToggle,
        switchAndEdit,
      };
    },
  };
</script>

<style scoped lang="scss">
  .notification-settings {
    &__intro {
      display: flex;
      align-items: flex-start;
      gap: 0.625rem;
      padding: 0.875rem 1rem;
      margin-bottom: 1.5rem;
      background: var(--brand-50);
      border: 1px solid var(--brand-100, #dbeafe);
      border-radius: 0.625rem;
      color: var(--brand-700);

      p {
        margin: 0;
        font-size: 0.8125rem;
        line-height: 1.5;
      }

      :deep(svg) {
        flex-shrink: 0;
        margin-top: 0.125rem;
      }
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 3rem 0;
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
  }

  .org-card {
    border: 1px solid var(--slate-200);
    border-radius: 0.75rem;
    background: var(--white);
    padding: 1.25rem;
    transition: border-color 0.15s ease;

    &--current {
      border-color: var(--brand-300, #93c5fd);
      box-shadow: 0 0 0 1px var(--brand-200, #bfdbfe);
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      margin-bottom: 1rem;
    }

    &__heading {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5rem;
      min-width: 0;
    }

    &__name {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    &__role {
      padding: 0.125rem 0.5rem;
      border-radius: 9999px;
      font-size: 0.6875rem;
      font-weight: 500;
      background: var(--slate-100);
      color: var(--slate-700);

      &--admin {
        background: var(--purple-100);
        color: var(--purple-700);
      }

      &--owner {
        background: var(--emerald-100, #d1fae5);
        color: var(--emerald-700, #047857);
      }

      &--member {
        background: var(--blue-100);
        color: var(--blue-700);
      }

      &--installer {
        background: var(--amber-100);
        color: var(--amber-700);
      }
    }

    &__badge {
      padding: 0.125rem 0.5rem;
      border-radius: 9999px;
      font-size: 0.6875rem;
      font-weight: 500;
      background: var(--brand-50);
      color: var(--brand-600);
      border: 1px solid var(--brand-200, #bfdbfe);
    }

    &__status {
      flex-shrink: 0;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-400);

      &--on {
        color: var(--brand-500);
      }
    }

    &__body {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__helper {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
      line-height: 1.5;
    }

    &__note {
      margin: 0;
      font-size: 0.75rem;
      color: var(--slate-500);

      &--muted {
        font-style: italic;
        color: var(--slate-400);
      }
    }

    &__switch {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-top: 0.5rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-100);
    }
  }
</style>
